import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { customerAuthApi, startGoogleAuth } from "@/lib/customerAuth";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import StoreHeader from "@/components/StoreHeader";

export default function CustomerLogin() {
  const { setUser } = useCustomerAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const initialError = location.state?.authError || "";

  const [tab, setTab] = useState("login");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ full_name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);

  const runLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const me = await customerAuthApi.login(login.email.trim(), login.password);
      setUser(me);
      nav(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.detail || "Sign in failed.");
    } finally {
      setBusy(false);
    }
  };

  const runRegister = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const me = await customerAuthApi.register({
        email: reg.email.trim(),
        password: reg.password,
        full_name: reg.full_name.trim(),
        phone: reg.phone.trim() || undefined,
      });
      setUser(me);
      nav(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.detail || "Could not create your account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="customer-login-root">
      <StoreHeader />
      <main className="max-w-md mx-auto px-6 py-14">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Customers</div>
        <h1 className="text-3xl font-semibold mt-1">Sign in to Fast Force</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Save your addresses, track orders and check out faster.
        </p>

        <Button
          type="button"
          size="lg"
          variant="outline"
          className="w-full mt-8 justify-center gap-3"
          onClick={() => startGoogleAuth(from)}
          data-testid="customer-login-google"
        >
          <GoogleIcon />
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px bg-border flex-1" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground">or</div>
          <div className="h-px bg-border flex-1" />
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="customer-login-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="customer-login-tab-signin">Sign in</TabsTrigger>
            <TabsTrigger value="register" data-testid="customer-login-tab-register">Create account</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={runLogin} className="space-y-4 mt-4" data-testid="customer-login-form">
              <Field label="Email">
                <Input required type="email" value={login.email}
                  onChange={(e) => setLogin({ ...login, email: e.target.value })}
                  data-testid="customer-login-email" />
              </Field>
              <Field label="Password">
                <Input required type="password" value={login.password}
                  onChange={(e) => setLogin({ ...login, password: e.target.value })}
                  data-testid="customer-login-password" />
              </Field>
              <Button type="submit" size="lg" className="w-full" disabled={busy}
                data-testid="customer-login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={runRegister} className="space-y-4 mt-4" data-testid="customer-register-form">
              <Field label="Full name">
                <Input required value={reg.full_name}
                  onChange={(e) => setReg({ ...reg, full_name: e.target.value })}
                  data-testid="customer-register-name" />
              </Field>
              <Field label="Email">
                <Input required type="email" value={reg.email}
                  onChange={(e) => setReg({ ...reg, email: e.target.value })}
                  data-testid="customer-register-email" />
              </Field>
              <Field label="Phone (optional)">
                <Input value={reg.phone} placeholder="+971…"
                  onChange={(e) => setReg({ ...reg, phone: e.target.value })}
                  data-testid="customer-register-phone" />
              </Field>
              <Field label="Password (min 8)">
                <Input required type="password" minLength={8} value={reg.password}
                  onChange={(e) => setReg({ ...reg, password: e.target.value })}
                  data-testid="customer-register-password" />
              </Field>
              <Button type="submit" size="lg" className="w-full" disabled={busy}
                data-testid="customer-register-submit">
                {busy ? "Creating account…" : "Create account"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="text-xs text-muted-foreground text-center mt-8">
          <Link to="/" className="hover:text-foreground" data-testid="customer-login-continue-guest">
            Continue as guest &rarr;
          </Link>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9c-1.9 1.4-4.4 2.4-6.9 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6 4.9C40.9 34.8 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
